import { useCallback, useEffect } from 'react';
import { useRecoilValue, useRecoilState } from 'recoil';
import shader from 'shader';
import { EXPANDED_CHAT_WIDTH } from '@/config/constants';
import { backgroundColorSelector, accentColorSelector, userConfigState, chatWindowSizeSelector, fontFamilySelector, variantSelector, } from '@/state/config';
export const variables = {
    '--moveo-z-index': '1100',
    '--moveo-shadow-message': '0 2px 2px rgba(0 0 0 4%)',
    '--moveo-shadow-bubble': '0px 0px 10px 0px #10182824',
    '--moveo-border-radius-base': '5px',
    '--moveo-font-primary': "'Inter', monospace",
    '--moveo-survey-overlay': '#8c8c8c',
    '--moveo-header-height': '55px',
    '--moveo-modal-tall-height': '600px',
    '--moveo-modal-compact-min-height': '112px',
    '--moveo-modal-phone-min-height': '54vh',
    '--moveo-modal-tall-width': 'clamp(300px, 400px, 450px)',
    '--moveo-modal-full-height': '80vh',
    '--moveo-modal-full-width': '80vw',
    '--moveo-modal-full-height-phone': '60vh',
    '--moveo-modal-header-height': '46px',
    '--moveo-modal-background-color': '#fff',
    '--moveo-webview-message-width': '220px',
    '--moveo-webview-message-height': '30px',
    '--moveo-webview-button-width': '80%',
    '--moveo-loadingIcon-size': '1rem',
    '--moveo-carousel-media-height': '130px',
    '--moveo-message-max-width': '251px',
    '--moveo-message-max-width-phone': '288px',
    '--moveo-message-min-width': '70px',
    '--moveo-dropdown-box-shadow': '0.3px 0.5px 0.7px hsl(0deg 0% 55% / 45%), 1.2px 1.8px 2.4px -0.8px hsl(0deg 0% 55% / 45%), 3px 4.7px 6.2px -1.7px hsl(0deg 0% 55% / 45%), 7.3px 11.6px 15.3px -2.5px hsl(0deg 0% 55% / 44%)',
    '--moveo-dropdown-width': '223px',
    '--moveo-survey-width': '89%',
    '--moveo-survey-max-width': '50%',
    '--moveo-survey-height': '80%',
    '--moveo-font-xs': '0.625rem',
    '--moveo-font-sm': '0.75rem',
    '--moveo-font-md': '0.875rem',
    '--moveo-font-lg': '1rem',
    '--nbg-bg': '#f5f8f6',
    //TODO check rename/replace old variables
    // New variables
    '--moveo-new-header-height': '68px',
    '--moveo-new-border-radius-base': '16px',
    '--moveo-expanded-chat-width': EXPANDED_CHAT_WIDTH,
};
const googleFontPath = (fontFamily) => `https://fonts.googleapis.com/css2?family=${fontFamily}:wght@300;400;600;700&display=swap`;
const configs = {
    nbg: [
        {
            fontFamily: 'Aptos',
            fontPath: '../public/fonts/aptos/Aptos-Bold-Italic.ttf',
            fontWeight: '700',
            fontStyle: 'italic',
        },
        {
            fontFamily: 'Aptos',
            fontPath: '../public/fonts/aptos/Aptos-Bold.ttf',
            fontWeight: '700',
            fontStyle: 'normal',
        },
        {
            fontFamily: 'Aptos',
            fontPath: '../public/fonts/aptos/Aptos-ExtraBold-Italic.ttf',
            fontWeight: '800',
            fontStyle: 'italic',
        },
        {
            fontFamily: 'Aptos',
            fontPath: '../public/fonts/aptos/Aptos-ExtraBold.ttf',
            fontWeight: '800',
            fontStyle: 'normal',
        },
        {
            fontFamily: 'Aptos',
            fontPath: '../public/fonts/aptos/Aptos-Italic.ttf',
            fontWeight: '400',
            fontStyle: 'italic',
        },
        {
            fontFamily: 'Aptos',
            fontPath: '../public/fonts/aptos/Aptos-Light-Italic.ttf',
            fontWeight: '300',
            fontStyle: 'italic',
        },
        {
            fontFamily: 'Aptos',
            fontPath: '../public/fonts/aptos/Aptos-Light.ttf',
            fontWeight: '300',
            fontStyle: 'normal',
        },
        {
            fontFamily: 'Aptos',
            fontPath: '../public/fonts/aptos/Aptos-SemiBold-Italic.ttf',
            fontWeight: '600',
            fontStyle: 'italic',
        },
        {
            fontFamily: 'Aptos',
            fontPath: '../public/fonts/aptos/Aptos-SemiBold.ttf',
            fontWeight: '600',
            fontStyle: 'normal',
        },
    ],
    default: {
        fontFamily: 'Inter',
        fontPath: googleFontPath('Inter'),
    },
};
export const useTheme = () => {
    const backgroundColor = useRecoilValue(backgroundColorSelector);
    const accentColor = useRecoilValue(accentColorSelector);
    const fontFamily = useRecoilValue(fontFamilySelector);
    const [widgetConfig, setWidgetConfig] = useRecoilState(userConfigState);
    const chatWindowSize = useRecoilValue(chatWindowSizeSelector);
    const element = document.querySelector('.appContainer') ||
        document.querySelector('body');
    const { isNbg } = useRecoilValue(variantSelector);
    const loadFont = useCallback((fontFamily, fontPath, fontWeight = 'normal', fontStyle = 'normal') => {
        const fontFace = new FontFace(fontFamily, `url(${fontPath})`, {
            weight: fontWeight,
            style: fontStyle,
        });
        fontFace.load().then((loadedFont) => {
            document.fonts.add(loadedFont);
            // Set default-font-family as primary for Tailwind/shadcn
            element === null || element === void 0 ? void 0 : element.style.setProperty('--default-font-family', `'${fontFamily}', sans-serif`);
            // Keep moveo-font-primary for backward compatibility
            element === null || element === void 0 ? void 0 : element.style.setProperty('--moveo-font-primary', `'${fontFamily}', sans-serif`);
        });
    }, [element === null || element === void 0 ? void 0 : element.style]);
    const loadGoogleFont = useCallback((fontFamily, fontPath) => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = fontPath;
        document.head.appendChild(link);
        // Set both variables for compatibility
        element === null || element === void 0 ? void 0 : element.style.setProperty('--default-font-family', `'${fontFamily}', sans-serif`);
        element === null || element === void 0 ? void 0 : element.style.setProperty('--moveo-font-primary', `'${fontFamily}', sans-serif`);
    }, [element === null || element === void 0 ? void 0 : element.style]);
    useEffect(() => {
        const initFont = async () => {
            try {
                // Load Google Font if specified in config
                if (fontFamily) {
                    const fontPath = googleFontPath(fontFamily);
                    loadGoogleFont(fontFamily, fontPath);
                    // Load NBG Font if variant is nbg
                }
                else if (isNbg) {
                    for (const font of configs.nbg) {
                        loadFont(font.fontFamily, font.fontPath, font.fontWeight, font.fontStyle);
                    }
                    // Load default font
                }
                else {
                    loadGoogleFont(configs.default.fontFamily, configs.default.fontPath);
                }
            }
            catch (_a) {
                loadGoogleFont(configs.default.fontFamily, configs.default.fontPath);
            }
        };
        initFont();
    }, [fontFamily, loadFont, loadGoogleFont, isNbg]);
    useEffect(() => {
        // Apply default variables
        Object.entries(variables).forEach(([key, value]) => {
            // If we have a custom font and this is the font-primary variable, use the custom font
            if (key !== '--moveo-font-primary') {
                element === null || element === void 0 ? void 0 : element.style.setProperty(key, value);
            }
        });
        if (chatWindowSize) {
            element === null || element === void 0 ? void 0 : element.style.setProperty('--moveo-chat-width', chatWindowSize === null || chatWindowSize === void 0 ? void 0 : chatWindowSize.width);
            element === null || element === void 0 ? void 0 : element.style.setProperty('--moveo-chat-height', chatWindowSize === null || chatWindowSize === void 0 ? void 0 : chatWindowSize.height);
        }
    }, [chatWindowSize, element === null || element === void 0 ? void 0 : element.style]);
    useEffect(() => {
        if (widgetConfig.theme === 'auto') {
            if (window.matchMedia &&
                window.matchMedia('(prefers-color-scheme: dark)').matches) {
                setWidgetConfig((prev) => (Object.assign(Object.assign({}, prev), { theme: 'dark' })));
            }
            else {
                setWidgetConfig((prev) => (Object.assign(Object.assign({}, prev), { theme: 'light' })));
            }
        }
        if (['light', 'dark', 'auto'].includes(widgetConfig.theme)) {
            element === null || element === void 0 ? void 0 : element.style.setProperty('--moveo-background-color', backgroundColor);
            element === null || element === void 0 ? void 0 : element.style.setProperty('--moveo-background-color-hover', shader(backgroundColor, -0.3));
            element === null || element === void 0 ? void 0 : element.style.setProperty('--moveo-accent-color', accentColor);
            /* New Colors */
            element === null || element === void 0 ? void 0 : element.style.setProperty('--color-accent', backgroundColor);
            element === null || element === void 0 ? void 0 : element.style.setProperty('--color-text-primary-foreground', accentColor);
        }
    }, [
        accentColor,
        backgroundColor,
        element === null || element === void 0 ? void 0 : element.style,
        setWidgetConfig,
        widgetConfig,
        widgetConfig.theme,
    ]);
};
